import axios from 'axios';
import axiosRetry from 'axios-retry';
import { omitBy } from 'lodash/fp';
import Lottie from 'lottie-react';
import { ComponentPropsWithoutRef, memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { clearAssetLoadingState, setAssetLoadingState } from 'inquiry/redux/actions/ui';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import handleAxiosError from 'lib/errors/handleAxiosError';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { ImageRemoteComponent } from 'lib/interfaces/ui-components';

type IMGWithoutSrc = Omit<ComponentPropsWithoutRef<'img'>, 'src'>;

/**
 * NOTE: we do not evict this cache. While this does run the risk of memory
 * leak, it's bounded by the number of images in an ITV, so shouldn't be a
 * problem.
 *
 * @private we only import this for specs.
 */
export const assetCache: Record<string, string | Record<string, unknown>> = {};

type ImageType = 'image' | 'svg' | 'json' | null;

function getImageType({
  contentType,
  normalizedPathname,
  src,
}: Pick<CachedImageProps, 'contentType' | 'src'> & { normalizedPathname: string }): ImageType {
  if (
    contentType === 'image/png' ||
    contentType === 'image/gif' ||
    contentType === 'image/jpeg' ||
    contentType === 'image/webp' ||
    normalizedPathname?.endsWith('.png') ||
    normalizedPathname?.endsWith('.gif') ||
    normalizedPathname?.endsWith('.jpg') ||
    normalizedPathname?.endsWith('.jpeg') ||
    normalizedPathname?.endsWith('.webp') ||
    src?.startsWith('data:image/png;') ||
    src?.startsWith('data:image/gif;') ||
    src?.startsWith('data:image/jpeg;') ||
    src?.startsWith('data:image/webp;')
  ) {
    return 'image';
  } else if (
    contentType === 'image/svg+xml' ||
    normalizedPathname?.endsWith('.svg') ||
    src?.startsWith('data:image/svg+xml')
  ) {
    return 'svg';
  } else if (contentType === 'application/json') {
    return 'json';
  } else {
    return null;
  }
}

export type CachedImageProps = IMGWithoutSrc & {
  className?: string;
  contentType?: string;
  'data-test'?: string;
  environment?: string;
  styles?: ImageRemoteComponent['styles'];

  // src was occasionally not set in production
  src?: string;
  onLoad?: () => void;
  onError?: () => void;
};

const CachedImage = memo(function CachedImageInner({
  contentType,
  src,
  styles,
  'data-test': dataTest = 'image',
  environment,
  alt,
  'aria-label': ariaLabel,
  ...props
}: CachedImageProps) {
  const [loading, { disable: disableLoading }] = useToggle(true);
  const dispatch = useDispatch();
  /**
   * We try to load images as blobs, so we can cache the object URLs.
   * If the network request fails, fall back to regular image loading.
   */
  const [asyncImageLoadFailed, { enable: setAsyncImageLoadFailed }] = useToggle(false);

  let pathname: string;
  try {
    // Absolute path -- remove query string
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pathname = new URL(src).pathname;
  } catch {
    // Relative paths cannot contain query strings
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pathname = src;
  }

  const [assetData, setAssetData] = useState<string | Record<string, unknown>>(
    assetCache[pathname],
  );

  const imageType = getImageType({
    contentType,
    normalizedPathname: pathname?.toLowerCase(),
    src,
  });
  const isDataUri = src?.toLowerCase().startsWith('data:');

  const loadStart = () => !!pathname && dispatch(setAssetLoadingState(pathname));
  const loadEnd = () => {
    !!pathname && dispatch(clearAssetLoadingState(pathname));
    disableLoading();
  };

  // NOTE: We can't early exit on cache hit because we don't currently validate
  // uniqueness of filename on the server. If/when we do, we can rely
  // entirely on the cache.
  useEffect(() => {
    // Only allow inline loading of Persona-hosted SVGs and Lottie files
    // Standard images can be loaded without issue
    const IsThirdPartyImage =
      (environment ? environment === 'production' : import.meta.env.PROD) &&
      !(
        src?.startsWith('https://files.withpersona.com/') ||
        src?.startsWith('https://files.withpersona-staging.com/')
      );

    if (IsThirdPartyImage) {
      loadEnd();
      return;
    }

    // We have to call the onLoad and onError handlers here to capture XHR load errors
    switch (imageType) {
      case 'image':
        // Data URIs can be eagerly rendered
        if (isDataUri) return;
        if (src == null) return;

        loadStart();

        axios
          .get(src, {
            responseType: 'blob',
            'axios-retry': {
              // We've seen that these are mainly network errors, likely timeouts from loading large as
              retryDelay: () => 0,
              // Connection timeout is not retried by default
              // https://github.com/softonic/axios-retry/blob/master/src/index.ts#L95
              retryCondition: (e) =>
                axiosRetry.isNetworkOrIdempotentRequestError(e) || e.code === 'ECONNABORTED',
            },
          })
          // Default encoding results in the file being unreadable.
          .then(({ data }) => {
            const blob = URL.createObjectURL(data);
            assetCache[pathname] = blob;
            setAssetData(blob);
            loadEnd();
            props.onLoad?.();
          })
          .catch((error) => {
            setAsyncImageLoadFailed();
            loadEnd();
            props.onError?.();
            handleAxiosError({
              error,
              method: 'GET',
              uri: src,
            });
          })
          .catch((error) => {
            if (error instanceof ApiNetworkError) {
              // ignore network errors when loading cached images
            } else {
              throw error;
            }
          });
        break;
      case 'svg':
        loadStart();
        // We fetch the SVG then use it as the src for an <img> rather than inlining to avoid XSS. We can't pass src
        // directly to the <img> because Rails-uploaded SVGs are reported as application/octet-stream.
        // https://github.com/rails/rails/issues/34665
        // These user-uploaded SVGs don't need to be themed or otherwise styled.
        axios
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .get(src)
          // Default encoding results in the file being unreadable.
          .then(({ data: svgString }) => {
            const processedSvgString = replaceColors(svgString, getColorsFromStyles(styles));
            const dataUri = `data:image/svg+xml;base64,${btoa(processedSvgString)}`;
            assetCache[pathname] = dataUri;
            setAssetData(dataUri);
            loadEnd();
            props.onLoad?.();
          })
          .catch((error) => {
            loadEnd();
            props.onError?.();
            handleAxiosError({
              error,
              method: 'GET',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              uri: src,
            });
          })
          .catch((error) => {
            if (error instanceof ApiNetworkError) {
              // ignore network errors when loading cached images
            } else {
              throw error;
            }
          });
        break;
      case 'json':
        loadStart();
        axios
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .get(src)
          .then(({ data }) => {
            // When Lottie receives new animation data, it restarts. Lottie
            // unfortunately mutates the asset data when it plays, so neither
            // shallow or deep equality works. We just check the name of the
            // animation as a heuristic to see if it changed.
            if (assetData == null || data?.nm !== (assetData as Record<string, unknown>)?.nm) {
              assetCache[pathname] = data;
              setAssetData(data);
            }

            loadEnd();
            props.onLoad?.();
          })
          .catch((error) => {
            loadEnd();
            props.onError?.();
            handleAxiosError({
              error,
              method: 'GET',
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              uri: src,
            });
          })
          .catch((error) => {
            if (error instanceof ApiNetworkError) {
              // ignore network errors when loading cached images
            } else {
              throw error;
            }
          });
        break;
      default:
        loadEnd();
        props.onError?.();
        break;
    }
  }, [src, imageType]);

  if (!src) {
    return null;
  }

  const imageLoading = (
    <div
      aria-label={loading ? 'loading image' : 'no image'}
      data-test={`${dataTest}__${loading ? 'loading' : 'empty'}`}
      className={props.className}
    />
  );

  switch (imageType) {
    case 'image':
      // Data URIs can be eagerly rendered
      if (isDataUri || asyncImageLoadFailed) {
        return <img {...props} src={src} data-test={dataTest} alt={alt} aria-label={ariaLabel} />;
      }
      return assetData != null ? (
        <img
          {...props}
          src={assetData as string}
          data-test={dataTest}
          alt={alt}
          aria-label={ariaLabel}
        />
      ) : (
        imageLoading
      );
    case 'svg':
      return assetData != null ? (
        <img
          {...props}
          src={assetData as string}
          data-test={dataTest}
          alt={alt}
          aria-label={ariaLabel}
        />
      ) : (
        imageLoading
      );
    case 'json':
      return assetData != null ? (
        <Lottie
          role='img'
          alt={alt}
          aria-label={ariaLabel}
          animationData={assetData as Record<string, unknown>}
          style={{
            height: sizeCss(styles?.height?.base?.base) ?? props.height ?? 'auto',
            width: sizeCss(styles?.width?.base?.base) ?? props.width ?? 'auto',
            // Context: INC-427
            // 100vw is important here to keep ImageRemote used for custom assets
            // inside absolutely positioned containers from arbitrarily sizing up,
            // which can cause perf issues if the asset is animated.
            maxWidth: 'min(100%, 100vw)',
          }}
          className={props.className}
        />
      ) : (
        imageLoading
      );
    default:
      return imageLoading;
  }
});

export default CachedImage;

// NOTE: Needs to be consistent across all platforms.
const REPLACEMENT_TOKENS = {
  fillColor: '{{ fill_color }}',
  backgroundColorColor: '{{ background_color }}',
  highlightColor: '{{ highlight_color }}',
  strokeColor: '{{ stroke_color }}',
};

interface SvgColors {
  fillColor?: string;
  backgroundColor?: string;
  highlightColor?: string;
  strokeColor?: string;
  originalFillColor?: string;
  originalBackgroundColor?: string;
  originalHighlightColor?: string;
  originalStrokeColor?: string;
}

function getColorsFromStyles(styles: ImageRemoteComponent['styles']): SvgColors {
  return {
    fillColor: colorCss(styles?.['fill-color']?.fill?.base),
    backgroundColor: colorCss(styles?.['fill-color']?.background?.base),
    highlightColor: colorCss(styles?.['fill-color']?.highlight?.base),
    strokeColor: colorCss(styles?.['stroke-color']?.stroke?.base),
    originalFillColor: colorCss(styles?.['fill-color']?.['original-fill']?.base),
    originalBackgroundColor: colorCss(styles?.['fill-color']?.['original-background']?.base),
    originalHighlightColor: colorCss(styles?.['fill-color']?.['original-highlight']?.base),
    originalStrokeColor: colorCss(styles?.['stroke-color']?.['original-stroke']?.base),
  };
}

/**
 * Supports theming static SVGs via SST. The server will send a list of
 * replacement colors, and optionally, a list of original colors.
 *
 * We use a 2-pass system to replace colors:
 * 1. Replace any provided original color with a static token (see
 * REPLACEMENT_TOKENS)
 * 2. Replace all static tokens with their corresponding replacement color.
 */
export function replaceColors(svgString: string, colors: SvgColors) {
  if (Object.values(colors).every((color) => color == null)) {
    return svgString;
  }

  // Pass 1: swap out original colors with tokens.
  const originalColorToTokenReplacementMap = omitBy((_, key) => key == null, {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [colors.originalFillColor?.toUpperCase()]: REPLACEMENT_TOKENS.fillColor,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [colors.originalBackgroundColor?.toUpperCase()]: REPLACEMENT_TOKENS.backgroundColorColor,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [colors.originalHighlightColor?.toUpperCase()]: REPLACEMENT_TOKENS.highlightColor,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    [colors.originalStrokeColor?.toUpperCase()]: REPLACEMENT_TOKENS.strokeColor,
  });

  let tokenizedSvgString = svgString;

  // Customers have the option of hardcoding tokens into their SVG directly,
  // and skipping Pass 1.
  if (Object.keys(originalColorToTokenReplacementMap).some((key) => key != null)) {
    const originalColorToTokenPattern = new RegExp(
      Object.keys(originalColorToTokenReplacementMap).join('|'),
      'gi',
    );

    tokenizedSvgString = svgString.replace(
      originalColorToTokenPattern,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (match) => originalColorToTokenReplacementMap[match.toUpperCase()],
    );
  }

  // Pass 2: swap out tokens with new colors.
  // Users can provide already tokenized assets to avoid configuring original colors.
  const tokenToNewColorReplacementMap = omitBy((value, _) => value == null, {
    [REPLACEMENT_TOKENS.fillColor]: colors.fillColor,
    [REPLACEMENT_TOKENS.backgroundColorColor]: colors.backgroundColor,
    [REPLACEMENT_TOKENS.highlightColor]: colors.highlightColor,
    [REPLACEMENT_TOKENS.strokeColor]: colors.strokeColor,
  });

  const tokenToNewColorPattern = new RegExp(
    Object.keys(tokenToNewColorReplacementMap).join('|'),
    'g',
  );

  // Replace all occurrences of placeholder tokens with new colors
  const finalSvgString = tokenizedSvgString.replace(
    tokenToNewColorPattern,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (match) => tokenToNewColorReplacementMap[match],
  );

  return finalSvgString;
}
